import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './ArchivedItemsManager.css';

import { useProtectedDataLock } from '../../context/SensitiveDataLockContext';

import { getPaymentModeLabel } from '../../utils/paymentModes';

const API_URL = import.meta.env.VITE_API_URL;

const ArchivedItemsManager = () => {
    const { isUnlocked, unlock, checkActionProtected, isActionVisible } = useProtectedDataLock();

    const [activeTab, setActiveTab] = useState('clients'); // 'clients' | 'dossiers' | 'payments'
    const [searchTerm, setSearchTerm] = useState('');
    const [archivedData, setArchivedData] = useState({ clients: [], dossiers: [], payments: [] });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return { headers: { Authorization: `Bearer ${token}` } };
    };

    const fetchArchivedItems = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/system-settings/archived-items`, getAuthHeaders());
            if (response.data?.success && response.data?.data) {
                setArchivedData(response.data.data);
            }
        } catch (err) {
            console.error('Erreur lors du chargement des éléments archivés:', err);
            Swal.fire('Erreur', 'Impossible de charger la liste des éléments archivés', 'error');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchArchivedItems();
    }, []);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchArchivedItems();
    };

    const handleRestore = async (itemType, params) => {
        const { clientId, dossierId, paymentId, label } = params;

        // Vérification de la protection par code secret (action ARCHIVE_RESTORE)
        if (checkActionProtected('ARCHIVE_RESTORE') && !isUnlocked) {
            const pwdResult = await Swal.fire({
                title: 'Code secret requis',
                text: 'La restauration d’éléments archivés est une action protégée. Veuillez saisir le code secret.',
                input: 'password',
                inputPlaceholder: 'Code secret de protection',
                showCancelButton: true,
                confirmButtonText: 'Déverrouiller et restaurer',
                cancelButtonText: 'Annuler',
                confirmButtonColor: '#10b981'
            });

            if (!pwdResult.isConfirmed || !pwdResult.value) return;
            const ok = await unlock(pwdResult.value);
            if (!ok) return;
        }

        const result = await Swal.fire({
            title: 'Restaurer cet élément ?',
            text: `Voulez-vous réactiver « ${label || ''} » ? Il sera réintégré dans la liste des éléments actifs.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Oui, restaurer',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#64748b'
        });

        if (!result.isConfirmed) return;

        try {
            const response = await axios.post(`${API_URL}/system-settings/restore-item`, {
                itemType,
                clientId,
                dossierId,
                paymentId
            }, getAuthHeaders());

            if (response.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Restauré !',
                    text: response.data.message || 'L’élément a été restauré avec succès.',
                    confirmButtonColor: '#10b981'
                });
                fetchArchivedItems();
            }
        } catch (err) {
            console.error('Erreur restauration:', err);
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de la restauration', 'error');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Filtres de recherche en direct
    const filterItems = (list, searchKeys) => {
        if (!list) return [];
        if (!searchTerm.trim()) return list;
        const term = searchTerm.toLowerCase();
        return list.filter(item =>
            searchKeys.some(key => {
                const val = key.split('.').reduce((obj, i) => obj?.[i], item);
                return val && String(val).toLowerCase().includes(term);
            })
        );
    };

    const filteredClients = filterItems(archivedData.clients, ['nom', 'prenom', 'cin', 'telephone', 'email']);
    const filteredDossiers = filterItems(archivedData.dossiers, ['referenceSteg', 'clientNom', 'deleteReason']);
    const filteredPayments = filterItems(archivedData.payments, ['clientNom', 'referenceSteg', 'modePaiement', 'notes']);

    return (
        <div className="aim-wrapper">
            {/* Header Card */}
            <div className="aim-header-card">
                <div className="aim-header-left">
                    <div className="aim-header-icon">📦</div>
                    <div className="aim-header-title">
                        <h2>Éléments Supprimés & Archivés</h2>
                        <p>Consultez et restaurez en 1-clic les clients, dossiers et paiements placés en corbeille (Soft Delete).</p>
                    </div>
                </div>
                <button onClick={handleRefresh} className="aim-refresh-btn" disabled={refreshing}>
                    <span style={{ display: 'inline-block', transform: refreshing ? 'rotate(360deg)' : 'none', transition: 'transform 0.5s ease' }}>🔄</span>
                    {refreshing ? 'Actualisation...' : 'Rafraîchir'}
                </button>
            </div>

            {/* Toolbar & Search */}
            <div className="aim-toolbar">
                <div className="aim-tabs">
                    {[
                        { id: 'clients', label: 'Clients archivés', icon: '👤', count: archivedData.clients?.length || 0 },
                        { id: 'dossiers', label: 'Dossiers archivés', icon: '📁', count: archivedData.dossiers?.length || 0 },
                        { id: 'payments', label: 'Paiements archivés', icon: '💸', count: archivedData.payments?.length || 0 }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            className={`aim-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <span>{tab.icon}</span>
                            <span>{tab.label}</span>
                            <span className="aim-tab-badge">{tab.count}</span>
                        </button>
                    ))}
                </div>

                <div className="aim-search-wrapper">
                    <span className="aim-search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Rechercher par nom, réf, cin..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="aim-search-input"
                    />
                </div>
            </div>

            {/* Content Table Card */}
            {loading ? (
                <div className="aim-loader-container">
                    <div className="aim-spinner" />
                    <p>Chargement du registre des archives...</p>
                </div>
            ) : (
                <div className="aim-table-card">

                    {/* CLIENTS TAB */}
                    {activeTab === 'clients' && (
                        filteredClients.length === 0 ? (
                            <div className="aim-empty-state">
                                <div className="aim-empty-icon">👤</div>
                                <div className="aim-empty-title">Aucun client archivé trouvé</div>
                                <div className="aim-empty-sub">
                                    {searchTerm ? 'Aucun résultat ne correspond à votre recherche.' : 'La corbeille des clients est actuellement vide.'}
                                </div>
                            </div>
                        ) : (
                            <table className="aim-table">
                                <thead>
                                    <tr>
                                        <th className="aim-th">Nom / Prénom</th>
                                        <th className="aim-th">CIN & Tél</th>
                                        <th className="aim-th">Date de suppression</th>
                                        <th className="aim-th">Motif</th>
                                        <th className="aim-th" style={{ textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredClients.map(c => (
                                        <tr key={c._id} className="aim-tr">
                                            <td className="aim-td">
                                                <div className="aim-cell-bold">{c.nom} {c.prenom || ''}</div>
                                                <div className="aim-cell-sub">{c.typeClient || 'Client BT'}</div>
                                            </td>
                                            <td className="aim-td">
                                                <div className="aim-cell-bold">{c.telephone || 'Non renseigné'}</div>
                                                <div className="aim-cell-sub">CIN: {c.cin || 'N/A'}</div>
                                            </td>
                                            <td className="aim-td aim-date-text">
                                                🗓️ {formatDate(c.deletedAt)}
                                            </td>
                                            <td className="aim-td aim-reason-italic">
                                                {c.deleteReason || 'Non spécifié'}
                                            </td>
                                            <td className="aim-td" style={{ textAlign: 'right' }}>
                                                {isActionVisible('ARCHIVE_RESTORE') && (
                                                    <button
                                                        onClick={() => handleRestore('client', { clientId: c._id, label: `${c.nom} ${c.prenom || ''}` })}
                                                        className="aim-restore-btn"
                                                    >
                                                        ♻️ Restaurer
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )
                    )}

                    {/* DOSSIERS TAB */}
                    {activeTab === 'dossiers' && (
                        filteredDossiers.length === 0 ? (
                            <div className="aim-empty-state">
                                <div className="aim-empty-icon">📁</div>
                                <div className="aim-empty-title">Aucun dossier archivé trouvé</div>
                                <div className="aim-empty-sub">
                                    {searchTerm ? 'Aucun résultat ne correspond à votre recherche.' : 'La corbeille des dossiers d’installation est vide.'}
                                </div>
                            </div>
                        ) : (
                            <table className="aim-table">
                                <thead>
                                    <tr>
                                        <th className="aim-th">Référence STEG</th>
                                        <th className="aim-th">Client</th>
                                        <th className="aim-th">Puissance / Montant</th>
                                        <th className="aim-th">Date de suppression</th>
                                        <th className="aim-th">Motif</th>
                                        <th className="aim-th" style={{ textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDossiers.map(d => (
                                        <tr key={d._id} className="aim-tr">
                                            <td className="aim-td">
                                                <div className="aim-cell-bold">{d.referenceSteg || 'Réf N/A'}</div>
                                                <div className="aim-cell-sub">Dossier technique</div>
                                            </td>
                                            <td className="aim-td">
                                                <div className="aim-cell-bold">{d.clientNom}</div>
                                            </td>
                                            <td className="aim-td">
                                                <span className="aim-amount-tag">{d.puissance || 0} kWc — {d.montantTotalPaiement || 0} DT</span>
                                            </td>
                                            <td className="aim-td aim-date-text">
                                                🗓️ {formatDate(d.deletedAt)}
                                            </td>
                                            <td className="aim-td aim-reason-italic">
                                                {d.deleteReason || 'Non spécifié'}
                                            </td>
                                            <td className="aim-td" style={{ textAlign: 'right' }}>
                                                {isActionVisible('ARCHIVE_RESTORE') && (
                                                    <button
                                                        onClick={() => handleRestore('dossier', { clientId: d.clientId, dossierId: d._id, label: `Dossier ${d.referenceSteg}` })}
                                                        className="aim-restore-btn"
                                                    >
                                                        ♻️ Restaurer
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )
                    )}

                    {/* PAYMENTS TAB */}
                    {activeTab === 'payments' && (
                        filteredPayments.length === 0 ? (
                            <div className="aim-empty-state">
                                <div className="aim-empty-icon">💸</div>
                                <div className="aim-empty-title">Aucun paiement archivé trouvé</div>
                                <div className="aim-empty-sub">
                                    {searchTerm ? 'Aucun résultat ne correspond à votre recherche.' : 'Aucun encaissement archivé dans le registre.'}
                                </div>
                            </div>
                        ) : (
                            <table className="aim-table">
                                <thead>
                                    <tr>
                                        <th className="aim-th">Client & Dossier</th>
                                        <th className="aim-th">Montant & Mode</th>
                                        <th className="aim-th">Date de suppression</th>
                                        <th className="aim-th">Notes / Remarques</th>
                                        <th className="aim-th" style={{ textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPayments.map(p => (
                                        <tr key={p._id} className="aim-tr">
                                            <td className="aim-td">
                                                <div className="aim-cell-bold">{p.clientNom}</div>
                                                <div className="aim-cell-sub">Réf STEG: {p.referenceSteg || 'N/A'}</div>
                                            </td>
                                            <td className="aim-td">
                                                <span className="aim-amount-tag">{p.montant} DT</span>
                                                <div className="aim-cell-sub" style={{ marginTop: '2px' }}>Mode: {getPaymentModeLabel(p.modePaiement)}</div>
                                            </td>
                                            <td className="aim-td aim-date-text">
                                                🗓️ {formatDate(p.dateSuppression)}
                                            </td>
                                            <td className="aim-td aim-reason-italic">
                                                {p.notes || 'Non spécifié'}
                                            </td>
                                            <td className="aim-td" style={{ textAlign: 'right' }}>
                                                {isActionVisible('ARCHIVE_RESTORE') && (
                                                    <button
                                                        onClick={() => handleRestore('payment', { clientId: p.clientId, dossierId: p.dossierId, paymentId: p._id, label: `Paiement ${p.montant} DT` })}
                                                        className="aim-restore-btn"
                                                    >
                                                        ♻️ Restaurer
                                                    </button>
                                                )}
                                            </td>

                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )
                    )}

                </div>
            )}
        </div>
    );
};

export default ArchivedItemsManager;
